import { apiClient as api } from "./httpClient";

// Instituições da entidade (RN-553): escolas, empresas, clubes, CAPS, CRAS, abrigos e afins com
// que os pacientes têm vínculo. Cadastro administrativo, permissão `institutions`. Os contatos do
// dia a dia ficam no vínculo do paciente, não aqui.

export type InstitutionKind = "institution" | "professional";

export interface Institution {
  id: string;
  name: string;
  address: string | null;
  number: string | null;
  neighborhood: string | null;
  zipCode: string | null;
  phone: string | null;
  email: string | null;
  director: string | null;
  supervisor: string | null;
  services: string | null;
  natureId: string | null;
  natureName: string | null;
  sphereId: string | null;
  sphereName: string | null;
  segmentId: string | null;
  segmentName: string | null;
  /** null = não informado; separado de "não é" de propósito */
  nonProfit: boolean | null;
  /** institution | professional (RN-553g) */
  kind: InstitutionKind;
  professionalTypeId: string | null;
  /** Categoria: a da lista da clínica, ou a escrita à mão na opção "Outra" */
  professionalTypeName: string | null;
  active: boolean;
  /** Quantos vínculos de paciente apontam para ela — a tela avisa antes de inativar */
  linkCount: number;
}

export interface SaveInstitutionPayload {
  id?: string | null;
  entityId: string;
  name: string;
  address?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  zipCode?: string | null;
  phone?: string | null;
  email?: string | null;
  director?: string | null;
  supervisor?: string | null;
  services?: string | null;
  natureId?: string | null;
  sphereId?: string | null;
  segmentId?: string | null;
  nonProfit?: boolean | null;
  kind?: InstitutionKind;
  professionalTypeId?: string | null;
  professionalTypeOther?: string | null;
}

/** Item dos três catálogos. `isPublic` só vem na natureza: é ele que liga a Esfera na tela. */
export interface InstitutionCatalogItem {
  id: string;
  name: string;
  order: number;
  active: boolean;
  isPublic: boolean;
  useCount: number;
}

export interface InstitutionCatalogs {
  natures: InstitutionCatalogItem[];
  spheres: InstitutionCatalogItem[];
  segments: InstitutionCatalogItem[];
  /** Categorias da clínica, para o cadastro do profissional externo (vêm por aqui para não
   *  esbarrar na permissão de configuração da rota própria) */
  professionalTypes: InstitutionCatalogItem[];
}

export type InstitutionCatalogKind = "nature" | "sphere" | "segment";

/** Linha que não entrou (ou entrou como atualização), com o motivo em PT-BR. */
export interface InstitutionImportIssue {
  row: number;
  name: string;
  reason: string;
}

export interface InstitutionImportResult {
  created: number;
  updated: number;
  skipped: number;
  issues: InstitutionImportIssue[];
}

export const institutionsService = {
  getByEntity: async (
    entityId: string,
    includeInactive = false,
    search?: string,
    kind?: InstitutionKind
  ): Promise<Institution[]> => {
    const response = await api.get(`/institutions/by-entity/${entityId}`, {
      params: { includeInactive, search: search || undefined, kind: kind || undefined },
    });
    return response.data;
  },

  /** Cria quando id vem vazio; edita quando vem preenchido — a tela é a mesma. */
  save: async (payload: SaveInstitutionPayload): Promise<string> => {
    const response = await api.post("/institutions", payload);
    return response.data?.id;
  },

  setActive: async (id: string, active: boolean): Promise<void> => {
    await api.patch(`/institutions/${id}/active`, { active });
  },

  /** Os três catálogos numa chamada só: combos do cadastro e a aba de cadastros. */
  catalogs: async (entityId: string): Promise<InstitutionCatalogs> => {
    const response = await api.get(`/institutions/catalogs/${entityId}`);
    return response.data;
  },

  saveCatalog: async (
    catalog: InstitutionCatalogKind,
    payload: { id?: string | null; entityId: string; name: string; order?: number }
  ): Promise<string> => {
    const response = await api.post(`/institutions/catalogs/${catalog}`, payload);
    return response.data?.id;
  },

  setCatalogActive: async (
    catalog: InstitutionCatalogKind,
    id: string,
    active: boolean
  ): Promise<void> => {
    await api.patch(`/institutions/catalogs/${catalog}/${id}/active`, { active });
  },

  /** Baixa o modelo (.xlsx) que o importador espera. */
  downloadTemplate: async (): Promise<void> => {
    const response = await api.get("/institutions/import-template", { responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modelo-instituicoes.xlsx";
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Sem o revoke o blob fica preso na memória da aba até o reload
    URL.revokeObjectURL(url);
  },

  /** Envia a planilha preenchida. Content-Type anulado: o axios precisa montar o boundary. */
  import: async (
    entityId: string,
    file: File,
    updateExisting: boolean
  ): Promise<InstitutionImportResult> => {
    const form = new FormData();
    form.append("file", file);
    form.append("entityId", entityId);
    form.append("updateExisting", String(updateExisting));
    const response = await api.post("/institutions/import", form, {
      headers: { "Content-Type": null },
      timeout: 120000,
    });
    return response.data;
  },
};

/** Erros de negócio conhecidos em PT-BR; o code vem do Result.Error do backend. */
export const mapInstitutionError = (error: any): string => {
  const code: string = error?.response?.data?.code ?? "";
  const messages: Record<string, string> = {
    "Institutions.NotFound": "Instituição não encontrada.",
    "Institutions.NameRequired": "Informe o nome da instituição.",
    "Institutions.DuplicatedName": "Já existe uma instituição ativa com esse nome.",
    "Institutions.ProfessionalTypeRequired": "Informe a categoria do profissional.",
    "Institutions.ProfessionalTypeAmbiguous":
      "Escolha a categoria da lista OU descreva outra, não as duas.",
    "Institutions.ProfessionalTypeNotFound": "Categoria profissional não encontrada ou inativa.",
    "Institutions.NatureRequired": "Informe a natureza da instituição.",
    "Institutions.SegmentRequired": "Informe o segmento da instituição.",
    "Institutions.SphereRequired":
      "Instituição pública precisa da esfera (municipal, estadual ou federal).",
    "Institutions.SphereNotAllowed": "Esfera só se aplica a instituição pública.",
    "Institutions.NatureNotFound": "Natureza não encontrada ou inativa.",
    "Institutions.SphereNotFound": "Esfera não encontrada ou inativa.",
    "Institutions.SegmentNotFound": "Segmento não encontrado ou inativo.",
    "Institutions.CatalogNameRequired": "Informe o nome.",
    "Institutions.CatalogNotFound": "Item do catálogo não encontrado.",
    "Institutions.CatalogInUse":
      "Este item está em uso por alguma instituição e não pode ser inativado.",
    "Institutions.ImportEmpty": "A planilha não tem nenhuma linha preenchida.",
    "Institutions.ImportFileRequired": "Escolha o arquivo da planilha.",
    "Institutions.ImportFileUnreadable":
      "Não foi possível ler a planilha. Use o modelo da tela (.xlsx ou .csv).",
    "Institutions.ImportHeaderMismatch":
      "A primeira linha não tem as colunas do modelo. Baixe o modelo e preencha a partir dele.",
    "Institutions.ImportTooManyRows":
      "A planilha tem linhas demais. Divida o arquivo e importe por partes.",
    "Institutions.InUse":
      "Esta instituição tem vínculo em curso de algum paciente. Encerre os vínculos antes de inativá-la.",
  };
  return messages[code] || "Não foi possível concluir a operação. Tente novamente.";
};
